/* SPACETRASH 4.0 — dependency-free storefront interactions. */
(() => {
  'use strict';

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  let lastDrawerTrigger = null;

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])'
  ].join(',');

  const setTriggerState = (drawerId, expanded) => {
    $$(`[data-drawer-open="${drawerId}"]`).forEach((trigger) => {
      trigger.setAttribute('aria-expanded', String(expanded));
    });
  };

  const openDrawer = (drawer, trigger = null) => {
    if (!drawer) return;
    lastDrawerTrigger = trigger || document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    setTriggerState(drawer.id, true);
    document.body.style.overflow = 'hidden';
    const closeButton = $('button[data-drawer-close]', drawer);
    const panel = $('.drawer__panel', drawer);
    (closeButton || panel)?.focus();
  };

  const closeDrawer = (drawer) => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    setTriggerState(drawer.id, false);
    if (!$('.drawer.is-open')) document.body.style.overflow = '';
    if (lastDrawerTrigger instanceof HTMLElement) lastDrawerTrigger.focus();
    lastDrawerTrigger = null;
  };

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest('[data-drawer-open]');
    if (openButton) {
      event.preventDefault();
      openDrawer($(`#${openButton.dataset.drawerOpen}`), openButton);
      return;
    }

    const closeButton = event.target.closest('[data-drawer-close]');
    if (closeButton) closeDrawer(closeButton.closest('.drawer'));
  });

  document.addEventListener('keydown', (event) => {
    const drawer = $('.drawer.is-open');
    if (!drawer) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      closeDrawer(drawer);
      return;
    }

    if (event.key !== 'Tab') return;
    const focusable = $$(focusableSelector, drawer).filter((element) => element.offsetParent !== null);
    if (!focusable.length) {
      event.preventDefault();
      $('.drawer__panel', drawer)?.focus();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  });

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-qty]');
    if (!button) return;
    const input = $('input', button.parentElement);
    if (!input) return;
    const step = button.dataset.qty === 'up' ? 1 : -1;
    input.value = Math.max(1, (parseInt(input.value, 10) || 1) + step);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });

  const formatMoney = (cents, currency) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currency || 'USD'
      }).format(Number(cents) / 100);
    } catch (error) {
      return `$${(Number(cents) / 100).toFixed(2)}`;
    }
  };

  /* 6.3.3: ask the Shopify CDN for an image about as wide as the main slot needs
     (theme assets and files both accept &width=), instead of the 1000–1254px originals. */
  const sizedPdpSrc = (src, image) => {
    if (!src || !/\/cdn\/shop\//.test(src)) return src;
    const slot = (image && image.clientWidth) || 400;
    const width = Math.min(1200, Math.max(400, Math.ceil(slot * (window.devicePixelRatio || 1) / 200) * 200));
    return /[?&]width=\d+/.test(src)
      ? src.replace(/([?&])width=\d+/, `$1width=${width}`)
      : `${src}${src.includes('?') ? '&' : '?'}width=${width}`;
  };
  const pdpDecoded = new Map();
  const decodePdpSrc = (src) => {
    if (!pdpDecoded.has(src)) {
      const probe = new Image();
      probe.decoding = 'async';
      probe.src = src;
      pdpDecoded.set(src, (probe.decode ? probe.decode() : Promise.resolve()).catch(() => {}));
    }
    return pdpDecoded.get(src);
  };
  window.stPdpPreload = (src, image) => decodePdpSrc(sizedPdpSrc(src, image));

  const setPdpMedia = ({ src, alt, cutout = false, thumbnail = null }, scope = document) => {
    const mainImage = $('[data-pdp-main]', scope);
    if (!mainImage) return;
    mainImage.closest('.pdp__media')?.dispatchEvent(new Event('st:product-zoom-reset'));
    /* 6.3.3 (INP): decode off the main thread first, then swap. Setting src directly made the
       next paint wait for a full-size decode (~0.6–1.3s per tap on mobile). */
    const bare = (url) => String(url || '').replace(/^https?:/, '').replace(/[?&]width=\d+/, '');
    const token = String(Date.now() + Math.random());
    mainImage.dataset.pdpPending = token;
    const sameImage = bare(mainImage.getAttribute('src')) === bare(src);
    const nextSrc = sameImage ? mainImage.getAttribute('src') : sizedPdpSrc(src, mainImage);
    (sameImage ? Promise.resolve() : decodePdpSrc(nextSrc)).then(() => {
      if (mainImage.dataset.pdpPending !== token) return;
      if (!sameImage) {
        mainImage.removeAttribute('srcset');
        mainImage.removeAttribute('sizes');
        mainImage.src = nextSrc;
      }
      mainImage.alt = alt || mainImage.alt;
      mainImage.classList.toggle('is-cutout', cutout);
      mainImage.dispatchEvent(new CustomEvent('st:pdp-media-swapped', { bubbles: true }));
    });
    if (thumbnail) {
      const thumbnails = $$('[data-thumb]', scope);
      thumbnails.forEach((button) => button.setAttribute('aria-current', String(button === thumbnail)));
      const visible = thumbnails.filter((button) => !button.hidden);
      const counter = $('[data-pdp-count]', scope);
      if (counter) counter.textContent = `${Math.max(0, visible.indexOf(thumbnail)) + 1} / ${visible.length}`;
      const strip = thumbnail.closest('.pdp__thumbs');
      if (strip && strip.scrollWidth > strip.clientWidth) {
        const delta = thumbnail.getBoundingClientRect().left - strip.getBoundingClientRect().left;
        strip.scrollTo({ left: strip.scrollLeft + delta - (strip.clientWidth - thumbnail.clientWidth) / 2, behavior: 'auto' });
      }
    }
  };

  const initProductForm = (form) => {
    const variantsElement = $('[data-product-variants]', form.parentElement);
    if (!variantsElement) return;

    let variants;
    try {
      variants = JSON.parse(variantsElement.textContent);
    } catch (error) {
      return;
    }

    const optionSelects = $$('[data-product-option-index]', form);
    if (!optionSelects.length) return;

    const updateVariant = () => {
      const selectedOptions = optionSelects.map((select) => select.value);
      const variant = variants.find((candidate) => (
        candidate.options.every((option, index) => option === selectedOptions[index])
      ));
      const idInput = $('[name="id"]', form);
      const addButton = $('[data-add-to-cart-button]', form);
      const status = $('[data-variant-status]');
      const inventoryRow = $('[data-variant-inventory-row]');
      const inventory = $('[data-variant-inventory]');
      const price = $('[data-pdp-price]');
      const currency = form.dataset.currency;

      if (!variant) {
        if (addButton) {
          addButton.disabled = true;
          addButton.textContent = 'Unavailable';
        }
        if (status) status.textContent = 'Unavailable';
        if (inventoryRow) inventoryRow.hidden = true;
        return;
      }

      if (idInput) {
        idInput.value = variant.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      if (addButton) {
        addButton.disabled = !variant.available;
        addButton.textContent = variant.available ? 'Add to cart' : 'Sold out';
      }
      if (status) status.textContent = variant.available ? 'Available' : 'Sold out';

      const quantity = Number(variant.inventory_quantity);
      const showInventory = variant.available && Number.isFinite(quantity) && quantity > 0 && quantity <= 100;
      if (inventoryRow) inventoryRow.hidden = !showInventory;
      if (inventory && showInventory) inventory.textContent = quantity;

      if (price) {
        const compare = Number(variant.compare_at_price);
        const current = Number(variant.price);
        price.innerHTML = compare > current
          ? `<s>${formatMoney(compare, currency)}</s> ${formatMoney(current, currency)}`
          : formatMoney(current, currency);
      }

      const pdpScope = form.closest('.pdp') || document;
      const allThumbnails = $$('[data-thumb]', pdpScope);
      const colorGallery = pdpScope.dataset?.pdpColorGallery === 'true';
      if (colorGallery) {
        const color = String(variant.option1 || variant.options[0] || '').toLowerCase();
        allThumbnails.forEach((button) => {
          button.hidden = Boolean(button.dataset.thumbColor && button.dataset.thumbColor !== color);
        });
      }
      const matchesVariant = (ids) => (ids || '').split(',').includes(String(variant.id));
      const thumbnail = allThumbnails.find((button) => matchesVariant(button.dataset.thumbVariantIds))
        || (colorGallery ? allThumbnails.find((button) => !button.hidden) : null);
      const image = variant.featured_image;
      if (image?.src || thumbnail) {
        const mainImage = $('[data-pdp-main]', pdpScope);
        if (mainImage) {
          const isObjectImage = matchesVariant(mainImage.dataset.pdpObjectVariantIds);
          setPdpMedia({
            src: thumbnail?.dataset.thumb || (isObjectImage ? mainImage.dataset.pdpObjectSrc : image.src),
            alt: thumbnail?.dataset.thumbAlt || image?.alt,
            cutout: thumbnail ? thumbnail.dataset.thumbCutout === 'true' : isObjectImage && mainImage.dataset.pdpObjectCutout === 'true',
            thumbnail
          }, pdpScope);
        }
      }
      if (colorGallery) pdpScope.querySelector('.pdp__media')?.dispatchEvent(new Event('st:pdp-gallery-filtered'));

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    };

    optionSelects.forEach((select) => select.addEventListener('change', updateVariant));
    updateVariant();
  };

  $$('form[data-product-form]').forEach(initProductForm);

  const refreshCart = async () => {
    const drawer = $('#cart-drawer');
    if (!drawer) return;

    const sectionResponse = await fetch(`${window.routes.cart_url}?section_id=cart-drawer`);
    if (!sectionResponse.ok) throw new Error('Cart section refresh failed');
    const html = await sectionResponse.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const currentPanel = $('.drawer__panel', drawer);
    const sectionContent = $('.shopify-section', documentFragment) || documentFragment.body;
    if (currentPanel && sectionContent) {
      currentPanel.innerHTML = sectionContent.innerHTML;
      // The product dialog renders its own gift card and is not inside this panel.
      // Leave it on "size" and a later checkout click still sees a size gate.
      const freshGift = currentPanel.querySelector('[data-founder-gift]');
      if (freshGift) {
        document.querySelectorAll('[data-founder-gift]').forEach((el) => {
          if (drawer.contains(el)) return;
          el.replaceWith(freshGift.cloneNode(true));
        });
      }
    }

    const cartResponse = await fetch(`${window.routes.cart_url}.js`, { cache: 'no-store' });
    if (!cartResponse.ok) throw new Error('Cart count refresh failed');
    const cart = await cartResponse.json();
    $$('[data-cart-count]').forEach((element) => {
      element.textContent = cart.item_count;
      element.hidden = cart.item_count === 0;
    });
    if (typeof window.__stUpdateCartSnapshot === 'function') window.__stUpdateCartSnapshot(cart);
    if (window.__stFounderGift) await window.__stFounderGift.syncCart(cart, cartResponse.headers.get('Date')).catch(() => {});
    if (window.__stFounderReveal) window.__stFounderReveal.sync(cart);
  };
  window.__stRefreshCart = refreshCart;

  const addToCart = async (form) => {
    const button = $('[data-add-to-cart-button]', form);
    const status = $('[data-cart-status]', form);
    const originalLabel = button?.textContent || 'Add to cart';
    if (button?.disabled) return;
    if (status) {
      status.textContent = '';
      status.classList.remove('form-note--err');
    }
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Adding…';
    }

    try {
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      if (!response.ok) throw new Error('Add to cart failed');
      await response.json();
      // Promotion bookkeeping cannot turn a successful add into a cart failure.
      if (window.__stFounderGift) {
        try { await window.__stFounderGift.afterSuccessfulAdd(response.headers.get('Date')); } catch (_) {}
      }
      await refreshCart();
      if (button) {
        button.classList.add('is-added');
        button.textContent = 'Added ✓ [Secured]';
      }
      if (status) status.textContent = 'Item added to your cart.';
      // 6.2: the quick view is a modal <dialog> (top layer) — close it first or the drawer opens behind it.
      const hostDialog = form.closest('dialog');
      if (hostDialog && hostDialog.open) hostDialog.close();
      openDrawer($('#cart-drawer'), hostDialog ? document.activeElement : button);
      if (window.__stFounderReveal) window.__stFounderReveal.afterAdd();
    } catch (error) {
      if (status) {
        status.classList.add('form-note--err');
        status.textContent = 'Unable to add this item. Please try again.';
      }
    } finally {
      window.setTimeout(() => {
        if (button) {
          button.classList.remove('is-added');
          button.removeAttribute('aria-busy');
          button.disabled = false;
          button.textContent = originalLabel;
        }
      }, 1200);
    }
  };

  // 2026-10-13: start the add on the submit button's click and never let the form submit.
  // Shopify reports product_added_to_cart for every submit of a /cart/add form — even one we
  // cancel, and before the add has happened — and again when the Ajax add succeeds. With both,
  // every add counted twice in Meta (pixel + CAPI) and GA4. Without the submit event only the
  // Ajax add is reported: once, and only when it worked. Covers the PDP button, the sticky bar
  // (form="pdp-form"), the quick view, and Enter in a field (the browser clicks the default button).
  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target && target.closest('button[type="submit"], input[type="submit"]');
    const form = button && button.form;
    if (!form || !form.matches('form[data-add-to-cart]')) return;
    event.preventDefault();
    addToCart(form);
  }, true);

  // Fallback for anything that still submits the form (requestSubmit, no submit button).
  document.addEventListener('submit', (event) => {
    const form = event.target.closest('form[data-add-to-cart]');
    if (!form) return;
    event.preventDefault();
    addToCart(form);
  });

  const changeCartLine = async (control, line, quantity) => {
    const errorMessage = $('[data-cart-error]');
    if (errorMessage) errorMessage.textContent = '';
    control.disabled = true;
    try {
      const response = await fetch(window.routes.cart_change_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity: Math.max(0, quantity) })
      });
      if (!response.ok) throw new Error('Cart change failed');
      if (document.body.classList.contains('template-cart')) window.location.reload();
      else await refreshCart();
    } catch (error) {
      control.disabled = false;
      if (errorMessage) errorMessage.textContent = 'Unable to update the cart. Please try again.';
    }
  };

  document.addEventListener('click', (event) => {
    const quantityButton = event.target.closest('[data-line-quantity]');
    if (quantityButton) {
      event.preventDefault();
      changeCartLine(
        quantityButton,
        Number(quantityButton.dataset.lineQuantity),
        Number(quantityButton.dataset.quantity)
      );
      return;
    }

    const removeButton = event.target.closest('[data-line-remove]');
    if (!removeButton) return;
    event.preventDefault();
    changeCartLine(removeButton, Number(removeButton.dataset.lineRemove), 0);
  });

  document.addEventListener('click', (event) => {
    const thumbnail = event.target.closest('[data-thumb]');
    if (!thumbnail) return;
    setPdpMedia({
      src: thumbnail.dataset.thumb,
      alt: thumbnail.dataset.thumbAlt,
      cutout: thumbnail.dataset.thumbCutout === 'true',
      thumbnail
    }, thumbnail.closest('.pdp') || document);
  });

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const saveData = navigator.connection?.saveData === true;
  if (reduceMotion || saveData) {
    $$('[data-motion-video]').forEach((video) => {
      video.pause();
      video.removeAttribute('autoplay');
    });
  }
})();

/* checkout_start_click (2026-09-25 fix, 2026-10-02 G1 2-for-1 gift tracking fix).
   Previously an inline <script> in snippets/cart-checkout-tracking.liquid bound listeners per surface.
   That never worked for the drawer's main path: the drawer only rendered the script when the cart already had items,
   and after an AJAX add-to-cart refreshCart() swaps in new HTML whose <script> never runs.
   One capture-phase listener on document survives every re-render. Event shape is unchanged (immediate + with_cart; still divide by 2).
   2026-10-02 G1 Fix:
   1. Support all 3 gift handles: bad-angle-earring-pink-armor, bad-angle-earring-pink-wound, cruciform-flower-ring.
   2. Pre-cache cart snapshot on load and on cart update so immediate click already includes cart data.
   3. Capture gift_add_click when user clicks any .cart-upsell__add button.
   2026-10-11 (6.3.7): since 10-05 the gift is a hidden $0 founders-gift-* product carrying the
   `_founder_gift` line property, with no discount allocation, so has_addon was always false.
   has_addon / gift_handle now recognise that line (gift_handle = the founders-gift-* handle);
   the old discounted real-product gift is still recognised for legacy carts. */
(() => {
  if (window.__stCheckoutTrack) return;
  window.__stCheckoutTrack = true;

  const giftHandles = ['bad-angle-earring-pink-armor', 'bad-angle-earring-pink-wound', 'cruciform-flower-ring'];
  const isGiftLine = (i) => Boolean(i && ((i.properties && i.properties._founder_gift) ||
    /^founders-gift-/.test(i.handle || '') ||
    (giftHandles.includes(i.handle) && (i.line_level_discount_allocations || []).length > 0)));

  const track = (params) => {
    if (typeof window.gtag === 'function') window.gtag('event', 'checkout_start_click', params);
  };

  let cachedSnapshot = null;

  const parseCart = (c) => {
    if (!c || typeof c !== 'object') return { cart_value: null, cart_items: null, has_addon: null, gift_handle: null };
    const giftItem = (c.items || []).find(isGiftLine);
    return {
      cart_value: Math.round(c.total_price || 0) / 100,
      cart_items: c.item_count || 0,
      has_addon: Boolean(giftItem),
      gift_handle: giftItem ? giftItem.handle : null,
    };
  };

  const fetchCartSnapshot = () =>
    fetch(`${(window.routes && window.routes.cart_url) || '/cart'}.js`, { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(async (r) => {
        if (!r.ok) throw new Error('Cart snapshot failed');
        const c = await r.json();
        if (window.__stFounderGift) window.__stFounderGift.syncCart(c, r.headers.get('Date')).catch(() => {});
        return c;
      })
      .then((c) => {
        cachedSnapshot = parseCart(c);
        return cachedSnapshot;
      })
      .catch(() => ({ cart_value: null, cart_items: null, has_addon: null, gift_handle: null }));

  // Expose cache updater for refreshCart
  window.__stUpdateCartSnapshot = (cart) => {
    cachedSnapshot = parseCart(cart);
  };

  // Pre-fetch cart snapshot on page load
  fetchCartSnapshot();

  // Wallet buttons live in a closed shadow root; composedPath is truncated there, so 'unknown' is expected.
  const guessWallet = (event) => {
    try {
      const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
      const blob = path.map((n) => (!n || !n.tagName) ? '' :
        `${n.tagName} ${typeof n.className === 'string' ? n.className : ''} ${n.getAttribute ? (n.getAttribute('aria-label') || '') : ''}`.toLowerCase()
      ).join(' ');
      if (blob.includes('shop-pay') || blob.includes('shoppay')) return 'shop_pay';
      if (blob.includes('paypal')) return 'paypal';
      if (blob.includes('google') || blob.includes('gpay')) return 'google_pay';
    } catch (e) { /* expected on closed shadow roots */ }
    return 'unknown';
  };

  const fire = (method, wallet, surface) => {
    const immediateData = Object.assign({ method, wallet, surface, detail: 'immediate' }, cachedSnapshot || {});
    track(immediateData);
    fetchCartSnapshot().then((snap) => track({ method, wallet, surface, detail: 'with_cart', ...snap }));
  };

  document.addEventListener('click', (event) => {
    const t = event.target;
    if (!(t instanceof Element)) return;
    const surface = t.closest('#cart-drawer') ? 'drawer' : 'cart';

    // Gift Add button click tracking (2026-10-02 G1 fix)
    const giftBtn = t.closest('.cart-upsell__add');
    if (giftBtn) {
      const giftHandle = giftBtn.getAttribute('data-gift-handle') || '';
      const giftVariant = giftBtn.getAttribute('data-gift-variant') || '';
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'gift_add_click', {
          gift_handle: giftHandle,
          gift_variant: giftVariant,
          surface: surface
        });
      }
      return;
    }

    // 6.3.9: a ring gift still waiting on a size is not a checkout. Only the surface
    // being clicked counts — a stale size card in the product dialog must not drop
    // checkout_start_click after the drawer has already revealed the gift.
    const checkoutControl = t.closest('.cart-express, [name="checkout"]');
    if (checkoutControl) {
      const surfaceRoot = t.closest('#cart-drawer') || checkoutControl.closest('form');
      if (surfaceRoot && surfaceRoot.querySelector('[data-founder-gift][data-fg="size"]')) return;
      if (checkoutControl.closest('.cart-express')) fire('express', guessWallet(event), surface);
      else fire('standard', 'none', surface);
    }
  }, true);
})();


/* ---- 6.1 (2026-10-01) ------------------------------------------------------
   (a) Product grid: on touch screens the first tap on a card image swaps to the
       worn image; a second tap follows the link. Desktop uses CSS :hover.
   (b) PDP: swipe the main image left/right to step through the thumbnails.
       Reuses the existing [data-thumb] click handler, so variant image logic stays in one place. */
(() => {
  const touchOnly = window.matchMedia('(hover: none)');
  const track = (name, params) => { if (typeof window.gtag === 'function') window.gtag('event', name, params); };

  // 6.2: tiles are <article class="product"> with the link on .product-photo (was a.card).
  document.addEventListener('click', (event) => {
    const open = document.querySelector('.product.is-worn, .card.is-worn');
    const ph = event.target.closest('[data-card-alt]');
    if (!ph) {
      if (open && !event.target.closest('.product, .card')) open.classList.remove('is-worn');
      return;
    }
    if (!touchOnly.matches) return;
    const card = ph.closest('.product, .card');
    if (!card || card.classList.contains('is-worn')) return; // second tap: follow the link
    event.preventDefault();
    if (open) open.classList.remove('is-worn');
    card.classList.add('is-worn');
    track('card_worn_tap', { product_url: ph.getAttribute('href') || card.getAttribute('href') || '' });
  }, true);

  const media = document.querySelector('.pdp__media');
  const allThumbs = Array.from(document.querySelectorAll('[data-thumb]'));
  if (!media || allThumbs.length < 2) return;
  const thumbs = () => allThumbs.filter((button) => !button.hidden);
  const counter = media.querySelector('[data-pdp-count]');
  const current = () => Math.max(0, thumbs().findIndex((b) => b.getAttribute('aria-current') === 'true'));
  const refreshCount = () => { if (counter) counter.textContent = (current() + 1) + ' / ' + thumbs().length; };
  allThumbs.forEach((b) => b.addEventListener('click', () => setTimeout(refreshCount, 0)));
  media.addEventListener('st:pdp-gallery-filtered', refreshCount);

  /* 6.3.3: warm the neighbours after load, and the whole gallery on the first touch of it,
     so a tap swaps to an already-decoded image. */
  const mainImg = media.querySelector('[data-pdp-main]');
  const preload = (i) => { const visible = thumbs(); const b = visible[(i + visible.length) % visible.length]; if (b && window.stPdpPreload) window.stPdpPreload(b.dataset.thumb, mainImg); };
  const idle = window.requestIdleCallback || ((fn) => setTimeout(fn, 1200));
  window.addEventListener('load', () => idle(() => { preload(current() + 1); preload(current() - 1); }), { once: true });
  let warmedGallery = '';
  const warmAll = () => {
    const visible = thumbs();
    const signature = visible.map((b) => b.dataset.thumb).join('|');
    if (warmedGallery === signature) return;
    warmedGallery = signature;
    visible.forEach((b, i) => preload(i));
  };
  media.parentElement.addEventListener('pointerdown', warmAll, { passive: true });
  media.parentElement.addEventListener('focusin', warmAll);

  let pendingDir = 0;
  const step = (dir) => {
    const visible = thumbs();
    if (!visible.length) return;
    pendingDir = dir;
    visible[(current() + dir + visible.length) % visible.length].click();
  };
  /* Slide-in plays when the decoded image is actually swapped in, not on the tap. */
  mainImg?.addEventListener('st:pdp-media-swapped', () => {
    if (!pendingDir) return;
    mainImg.classList.remove('is-in-next', 'is-in-prev');
    void mainImg.offsetWidth;
    mainImg.classList.add(pendingDir > 0 ? 'is-in-next' : 'is-in-prev');
    pendingDir = 0;
  });
  media.querySelector('[data-pdp-gallery-prev]')?.addEventListener('click', () => step(-1));
  media.querySelector('[data-pdp-gallery-next]')?.addEventListener('click', () => step(1));
  media.tabIndex = 0;
  media.setAttribute('role', 'group');
  media.setAttribute('aria-label', 'Product image gallery');
  const pdpGallery = media.parentElement;
  pdpGallery.addEventListener('keydown', (event) => {
    if (!event.target.closest('.pdp__media, .pdp__thumbs') || event.altKey || event.ctrlKey || event.metaKey) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    step(event.key === 'ArrowRight' ? 1 : -1);
  });

  let x0 = null, y0 = null, t0 = 0, swipes = 0;
  media.addEventListener('touchstart', (e) => {
    if (media.classList.contains('is-product-zoomed') || e.target.closest('button, a')) { x0 = null; return; }
    if (e.touches.length !== 1) { x0 = null; return; }
    x0 = e.touches[0].clientX; y0 = e.touches[0].clientY; t0 = Date.now();
  }, { passive: true });
  media.addEventListener('touchend', (e) => {
    if (media.classList.contains('is-product-zoomed')) { x0 = null; return; }
    if (x0 === null || !e.changedTouches.length) return;
    const dx = e.changedTouches[0].clientX - x0, dy = e.changedTouches[0].clientY - y0;
    x0 = null;
    if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.2 || Date.now() - t0 > 900) return;
    const dir = dx < 0 ? 1 : -1;
    step(dir);
    swipes += 1;
    if (swipes === 1) track('pdp_swipe', { product_handle: location.pathname.split('/products/')[1] || '' });
  }, { passive: true });
  media.addEventListener('touchcancel', () => { x0 = null; }, { passive: true });
})();


/* ---- 6.1b (2026-10-01): custom option dropdown ------------------------------
   Enhances every select[data-product-option-index] on the PDP. The native select stays the
   source of truth: picking an option sets select.value and dispatches `change`, so the
   existing variant logic (id / price / status / URL) is unchanged. Keyboard: Enter/Space/
   ArrowDown opens, arrows move, Enter/Space picks, Esc closes. */
(() => {
  const selects = document.querySelectorAll('select[data-product-option-index]:not([data-no-dd])');
  if (!selects.length) return;
  let variants = [];
  try { variants = JSON.parse(document.querySelector('[data-product-variants]')?.textContent || '[]'); } catch (e) {}
  const RING_DIAMETER = { 'US 7': 'Ø 0.68 in', 'US 9': 'Ø 0.74 in' }; // same numbers as .pdp__sizeguide (10-13: inches only, was 17.3 / 18.9 mm)
  let uid = 0;

  selects.forEach((select) => {
    const idx = Number(select.dataset.productOptionIndex);
    const isSize = (select.dataset.optionName || '').toLowerCase() === 'size';
    const wrap = document.createElement('div');
    wrap.className = 'st-dd';
    const listId = 'st-dd-list-' + (++uid);
    wrap.innerHTML = '<button type="button" class="st-dd__btn" aria-haspopup="listbox" aria-expanded="false" aria-controls="' + listId + '">' +
      '<span class="st-dd__val"></span><span class="st-dd__chev" aria-hidden="true"></span></button>' +
      '<ul class="st-dd__list" id="' + listId + '" role="listbox" tabindex="-1"></ul>';
    const btn = wrap.querySelector('.st-dd__btn');
    const list = wrap.querySelector('.st-dd__list');
    const val = wrap.querySelector('.st-dd__val');
    const label = document.querySelector('label[for="' + select.id + '"]');
    if (label) { label.id = label.id || select.id + '-label'; btn.setAttribute('aria-labelledby', label.id + ' ' + listId + '-cur'); list.setAttribute('aria-labelledby', label.id); }

    const soldOut = (value) => {
      const hits = variants.filter((v) => v.options && v.options[idx] === value);
      return hits.length > 0 && hits.every((v) => !v.available);
    };
    const subFor = (value) => soldOut(value) ? 'Sold out' : (isSize ? (RING_DIAMETER[value] || '') : '');

    const opts = Array.from(select.options).map((o, i) => {
      const li = document.createElement('li');
      li.className = 'st-dd__opt';
      li.id = listId + '-o' + i;
      li.setAttribute('role', 'option');
      li.dataset.value = o.value;
      li.style.setProperty('--i', i);
      const sub = subFor(o.value);
      li.innerHTML = '<span class="st-dd__name"></span>' + (sub ? '<span class="st-dd__sub"></span>' : '');
      li.querySelector('.st-dd__name').textContent = o.textContent.trim();
      if (sub) li.querySelector('.st-dd__sub').textContent = sub;
      list.appendChild(li);
      return li;
    });

    let active = -1;
    const render = (animate) => {
      const cur = select.value;
      opts.forEach((li) => li.setAttribute('aria-selected', String(li.dataset.value === cur)));
      const sub = subFor(cur);
      val.innerHTML = '<span id="' + listId + '-cur"></span>' + (sub ? '<span class="st-dd__sub"></span>' : '');
      val.firstChild.textContent = cur;
      if (sub) val.lastChild.textContent = sub;
      if (animate) { val.classList.remove('is-swap'); void val.offsetWidth; val.classList.add('is-swap'); }
    };
    const setActive = (i) => {
      active = (i + opts.length) % opts.length;
      opts.forEach((li, k) => li.classList.toggle('is-active', k === active));
      list.setAttribute('aria-activedescendant', opts[active].id);
    };
    const open = () => {
      document.querySelectorAll('.st-dd.is-open').forEach((d) => d !== wrap && d.dispatchEvent(new CustomEvent('st-dd-close')));
      opts.forEach((li) => li.classList.remove('is-picked'));
      wrap.classList.add('is-open');
      btn.setAttribute('aria-expanded', 'true');
      setActive(Math.max(0, opts.findIndex((li) => li.dataset.value === select.value)));
      list.focus({ preventScroll: true });
    };
    const close = (refocus) => {
      wrap.classList.remove('is-open');
      btn.setAttribute('aria-expanded', 'false');
      opts.forEach((li) => li.classList.remove('is-active'));
      if (refocus) btn.focus({ preventScroll: true });
    };
    const pick = (i) => {
      const li = opts[i];
      const changed = li.dataset.value !== select.value;
      li.classList.add('is-picked');
      if (changed) {
        select.value = li.dataset.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      setTimeout(() => { close(true); render(changed); }, 170);
    };

    wrap.addEventListener('st-dd-close', () => close(false));
    btn.addEventListener('click', () => (wrap.classList.contains('is-open') ? close(true) : open()));
    btn.addEventListener('keydown', (e) => {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); open(); }
    });
    list.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(active + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active - 1); }
      else if (e.key === 'Home') { e.preventDefault(); setActive(0); }
      else if (e.key === 'End') { e.preventDefault(); setActive(opts.length - 1); }
      else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); pick(active); }
      else if (e.key === 'Escape') { e.preventDefault(); close(true); }
      else if (e.key === 'Tab') { close(false); }
    });
    opts.forEach((li, i) => {
      li.addEventListener('pointerenter', () => setActive(i));
      li.addEventListener('click', () => pick(i));
    });
    document.addEventListener('click', (e) => { if (wrap.classList.contains('is-open') && !wrap.contains(e.target)) close(false); });
    select.addEventListener('change', () => render(false));

    select.classList.add('is-enhanced');
    select.setAttribute('tabindex', '-1');
    select.setAttribute('aria-hidden', 'true');
    select.after(wrap);
    render(false);
  });
})();


/* ---- 6.2 (2026-10-01): creative redesign interactions -------------------------------
   Ported from the creative demo script.js (r6-mobile-20261001) and wired to the store:
   - every link keeps a real href (collections / pages / PDP); JS only enhances it
   - "Visit store" became the cart, product dialog gained size + add to cart
   - interactive type is scoped to [data-st-interactive] so PDP / cart text stays still */
(() => {
  'use strict';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mqMobile = window.matchMedia('(max-width:700px)');
  const track = (name, params) => { if (typeof window.gtag === 'function') window.gtag('event', name, params); };
  const behavior = () => (reducedMotion.matches ? 'auto' : 'smooth');
  const plainClick = (event) => !(event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey);

  /* Olive page wipe (demo: page-transition) ---------------------------------------- */
  const transitionLayer = document.createElement('div');
  transitionLayer.className = 'page-transition';
  transitionLayer.setAttribute('aria-hidden', 'true');
  const transitionWordmark = document.createElement('span');
  transitionWordmark.textContent = 'SPACETRASH';
  transitionLayer.append(transitionWordmark);
  document.body.append(transitionLayer);
  let changingPage = false;

  const afterWipe = () => new Promise((resolve) => {
    const done = () => { clearTimeout(timer); transitionLayer.removeEventListener('transitionend', done); resolve(); };
    const timer = setTimeout(done, 420);
    transitionLayer.addEventListener('transitionend', done, { once: true });
  });
  const revealPage = () => {
    transitionLayer.className = 'page-transition is-uncovering';
    setTimeout(() => { transitionLayer.className = 'page-transition'; changingPage = false; }, 420);
  };
  const changePage = async (action, leavesDocument = false) => {
    if (changingPage) return;
    if (reducedMotion.matches) { action(); return; }
    changingPage = true;
    transitionLayer.className = 'page-transition is-covering';
    await afterWipe();
    try { action(); } finally { if (!leavesDocument) revealPage(); }
  };
  try {
    if (sessionStorage.getItem('spacetrash-page-wipe') === '1') {
      sessionStorage.removeItem('spacetrash-page-wipe');
      if (!reducedMotion.matches) {
        changingPage = true;
        transitionLayer.className = 'page-transition is-covering';
        requestAnimationFrame(() => requestAnimationFrame(revealPage));
      }
    }
  } catch (error) { /* navigation works without session storage */ }
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) { transitionLayer.className = 'page-transition'; changingPage = false; }
  });
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[data-transition]');
    if (!link || !plainClick(event) || link.target === '_blank' || link.hasAttribute('download')) return;
    const destination = new URL(link.href, location.href);
    if (destination.origin !== location.origin) return;
    if (destination.pathname === location.pathname && destination.hash) return;
    event.preventDefault();
    changePage(() => {
      try { sessionStorage.setItem('spacetrash-page-wipe', '1'); } catch (error) { /* optional entry wipe */ }
      location.assign(destination.href);
    }, true);
  });

  /* Shop mega menu (desktop hover/click, mobile toggle) ----------------------------- */
  const siteHeader = document.querySelector('[data-site-header]');
  const shopMenu = document.getElementById('shop-menu');
  const shopToggle = document.getElementById('shop-toggle');
  const mobileToggle = siteHeader ? siteHeader.querySelector('.mobile-menu-toggle') : null;
  const setMenuState = (open) => {
    if (!shopMenu) return;
    shopMenu.hidden = !open;
    [shopToggle, mobileToggle].filter(Boolean).forEach((t) => t.setAttribute('aria-expanded', String(open)));
    if (mobileToggle) mobileToggle.setAttribute('aria-label', open ? 'Close navigation' : 'Open navigation');
  };
  const openShop = () => setMenuState(true);
  const closeShop = () => setMenuState(false);
  if (siteHeader && shopMenu) {
    mobileToggle?.addEventListener('click', () => (shopMenu.hidden ? openShop() : closeShop()));
    shopToggle?.addEventListener('click', (event) => {
      if (event.pointerType === 'mouse' && window.matchMedia('(hover: hover)').matches) openShop();
      else if (shopMenu.hidden) openShop(); else closeShop();
    });
    shopToggle?.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') openShop(); });
    siteHeader.addEventListener('pointerleave', (event) => { if (event.pointerType === 'mouse' && !mqMobile.matches) closeShop(); });
    shopMenu.querySelector('.menu-close')?.addEventListener('click', closeShop);
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !shopMenu.hidden) { closeShop(); (mqMobile.matches ? mobileToggle : shopToggle)?.focus(); }
    });
    siteHeader.addEventListener('focusout', (event) => { if (!siteHeader.contains(event.relatedTarget)) closeShop(); });
    document.addEventListener('click', (event) => { if (!siteHeader.contains(event.target)) closeShop(); });
  }

  /* Homepage objects: category filter + "View all objects" drawer ------------------- */
  const objects = document.querySelector('[data-st-objects]');
  const catalogDrawer = document.getElementById('all-objects');
  const catalogToggle = objects ? objects.querySelector('.catalog-toggle') : null;
  if (objects) {
    const grid = objects.querySelector('[data-objects-grid]');
    const titleEl = objects.querySelector('#collection-title');
    const showAll = objects.querySelector('[data-show-all]');
    const empty = objects.querySelector('[data-objects-empty]');
    const titles = {
      all: objects.dataset.titleAll || 'Best sellers', rings: 'Rings', earrings: 'Earrings', bracelets: 'Bracelets',
      necklaces: 'Necklaces', brain: 'Brain Stimulation', angel: 'Bad Angel', entropic: 'Entropic Singularity', orbital: 'Orbital Bloom'
    };
    const pad = (n) => String(n).padStart(2, '0');
    const filterObjects = (category) => {
      if (!Object.prototype.hasOwnProperty.call(titles, category)) category = 'all';
      grid.querySelectorAll('[data-clone]').forEach((node) => node.remove());
      const featured = Array.from(grid.querySelectorAll('.product[data-featured]'));
      let shown = 0;
      if (category === 'all') {
        featured.forEach((node) => { node.hidden = false; });
        shown = featured.length;
      } else {
        featured.forEach((node) => { node.hidden = true; });
        const pool = catalogDrawer ? Array.from(catalogDrawer.querySelectorAll('.product')) : featured;
        pool.filter((node) => node.dataset.kind === category || node.dataset.series === category).forEach((node) => {
          const clone = node.cloneNode(true);
          clone.dataset.clone = '1';
          clone.hidden = false;
          clone.classList.remove('catalog-product');
          shown += 1;
          const number = clone.querySelector('.object-number');
          if (number) number.textContent = pad(shown);
          grid.append(clone);
        });
      }
      if (titleEl) titleEl.textContent = titles[category];
      if (showAll) showAll.hidden = category === 'all';
      if (empty) empty.hidden = shown > 0;
      document.querySelectorAll('[data-category]').forEach((link) => {
        if (link.dataset.category === category && category !== 'all') link.setAttribute('aria-current', 'true');
        else link.removeAttribute('aria-current');
      });
      if (category !== 'all') track('home_filter', { category });
    };
    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-category]');
      if (!link || !plainClick(event)) return;
      event.preventDefault();
      filterObjects(link.dataset.category);
      closeShop();
      objects.scrollIntoView({ behavior: behavior(), block: 'start' });
    });
    showAll?.addEventListener('click', () => filterObjects('all'));
    const initial = new URLSearchParams(location.search).get('category');
    if (initial) filterObjects(initial);
  }

  if (catalogDrawer && catalogToggle) {
    const labelOpen = catalogToggle.dataset.labelOpen || 'View all objects';
    const labelClose = catalogToggle.dataset.labelClose || 'Hide all objects';
    const setCatalog = (open, scroll = false) => {
      catalogDrawer.hidden = !open;
      catalogToggle.setAttribute('aria-expanded', String(open));
      catalogToggle.innerHTML = `${open ? labelClose : labelOpen} <span aria-hidden="true">+</span>`;
      if (open && scroll) catalogDrawer.scrollIntoView({ behavior: behavior(), block: 'start' });
      if (!open) catalogToggle.focus({ preventScroll: true });
      if (open) track('home_view_all', {});
    };
    catalogToggle.addEventListener('click', () => setCatalog(catalogDrawer.hidden, true));
    catalogDrawer.querySelector('[data-close-catalog]')?.addEventListener('click', () => setCatalog(false));
    document.addEventListener('click', (event) => {
      const link = event.target.closest('[data-open-catalog]');
      if (!link || !plainClick(event)) return;
      event.preventDefault();
      closeShop();
      setCatalog(true, true);
    });
    if (location.hash === '#all-objects') setCatalog(true, true);
  }

  /* Product quick view (demo: product-dialog) + add to cart ------------------------- */
  const dialog = document.getElementById('product-dialog');
  if (dialog) {
    const image = dialog.querySelector('#dialog-image');
    const galleryMedia = dialog.querySelector('.dialog-gallery__media');
    const galleryCount = dialog.querySelector('[data-qv-gallery-count]');
    const playback = dialog.querySelector('[data-qv-gallery-playback]');
    const galleryArrows = dialog.querySelectorAll('[data-qv-gallery-prev], [data-qv-gallery-next]');
    const switcher = dialog.querySelector('.image-switch');
    const sizes = dialog.querySelector('#dialog-sizes');
    const variantInput = dialog.querySelector('#dialog-variant');
    const addButton = dialog.querySelector('#dialog-add');
    const status = dialog.querySelector('[data-cart-status]');
    let opener = null;
    let active = null;
    let gallery = [];
    let imageIndex = 0;
    let slideshowTimer = null;
    let pausedByUser = false;
    let galleryGesture = false;

    const stopAutoplay = () => {
      clearTimeout(slideshowTimer);
      slideshowTimer = null;
    };
    const updatePlayback = () => {
      if (!playback) return;
      playback.hidden = gallery.length < 2;
      const zoomed = galleryMedia?.classList.contains('is-product-zoomed');
      playback.disabled = Boolean(zoomed);
      playback.textContent = zoomed ? 'Paused while zoomed' : pausedByUser ? 'Play' : 'Pause';
      playback.setAttribute('aria-label', zoomed ? 'Slideshow paused while image is zoomed' : pausedByUser ? 'Play image slideshow' : 'Pause image slideshow');
      playback.setAttribute('aria-pressed', String(pausedByUser || zoomed));
    };
    const scheduleAutoplay = () => {
      stopAutoplay();
      if (!dialog.open || !active || gallery.length < 2 || pausedByUser || galleryGesture || galleryMedia?.classList.contains('is-product-zoomed') || document.hidden) return;
      slideshowTimer = setTimeout(() => {
        showImage(imageIndex + 1);
        scheduleAutoplay();
      }, 5000);
    };
    const showImage = (index) => {
      if (!active || !gallery.length) return;
      galleryMedia?.dispatchEvent(new Event('st:product-zoom-reset'));
      imageIndex = (index + gallery.length) % gallery.length;
      const slide = gallery[imageIndex];
      image.src = slide.src;
      image.alt = slide.alt || active.fullTitle;
      image.classList.toggle('object-view', slide.view === 'object');
      image.classList.toggle('is-cutout', !!slide.cutout);
      image.style.cssText = slide.cutout && slide.objectStyle ? slide.objectStyle : '';
      if (galleryCount) {
        galleryCount.textContent = `${imageIndex + 1} / ${gallery.length}`;
        galleryCount.hidden = gallery.length < 2;
      }
      dialog.querySelectorAll('[data-image-view]').forEach((control) => {
        control.setAttribute('aria-pressed', String(control.dataset.imageView === slide.view));
      });
      if (gallery.length > 1) {
        const next = new Image();
        next.src = gallery[(imageIndex + 1) % gallery.length].src;
      }
    };
    const stepGallery = (direction) => {
      showImage(imageIndex + direction);
      scheduleAutoplay();
    };

    const showView = (worn) => {
      if (!active) return;
      const index = gallery.findIndex((slide) => slide.view === (worn ? 'worn' : 'object'));
      showImage(index < 0 ? 0 : index);
      scheduleAutoplay();
    };
    const pickVariant = (id) => {
      variantInput.value = id;
      sizes.querySelectorAll('.size-option').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.variantId === String(id))));
      const v = active.variants.find((x) => String(x.id) === String(id));
      addButton.disabled = !v || !v.available;
      addButton.textContent = v && v.available ? addButton.dataset.labelAdd : addButton.dataset.labelSoldOut;
    };
    const fill = (data) => {
      stopAutoplay();
      active = data;
      gallery = Array.isArray(data.gallery) ? data.gallery.filter((slide) => slide.src) : [];
      if (!gallery.length) {
        if (data.image) gallery.push({ src: data.image, alt: data.fullTitle, view: 'object', cutout: data.cutout, objectStyle: data.objectStyle });
        if (data.worn && data.worn !== data.image) gallery.push({ src: data.worn, alt: `${data.fullTitle} worn`, view: 'worn', cutout: false });
      }
      pausedByUser = reducedMotion.matches;
      galleryGesture = false;
      galleryArrows.forEach((button) => { button.hidden = gallery.length < 2; });
      if (galleryMedia) galleryMedia.hidden = !gallery.length;
      updatePlayback();
      dialog.dataset.handle = data.handle;
      dialog.querySelector('#dialog-collection').textContent = data.series || '';
      dialog.querySelector('#dialog-title').textContent = data.title;
      dialog.querySelector('#dialog-price').innerHTML = data.priceHtml;
      // 10-25: US shipping is free from $29 ($9.99 under); pieces under it say so in the small print.
      const note = dialog.querySelector('[data-dialog-note]');
      if (note) {
        const cents = Number(data.price);
        note.textContent = cents > 0 && cents < 2900 ? 'US shipping $9.99, free on orders $29+ · 30-day returns.' : (note.dataset.noteDefault || note.textContent);
      }
      dialog.querySelector('#dialog-link').href = data.url;
      if (status) { status.textContent = ''; status.classList.remove('form-note--err'); }
      switcher.hidden = !gallery.some((slide) => slide.view === 'worn');
      showView(!mqMobile.matches && !switcher.hidden);
      sizes.innerHTML = '';
      const firstAvailable = data.variants.find((v) => v.available) || data.variants[0];
      if (!data.single && data.variants.length > 1) {
        const label = document.createElement('span');
        label.className = 'dialog-sizes__label';
        label.textContent = data.optionName || 'Size';
        sizes.append(label);
        data.variants.forEach((v) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'size-option';
          b.dataset.variantId = v.id;
          b.textContent = v.title;
          b.disabled = !v.available;
          b.addEventListener('click', () => pickVariant(v.id));
          sizes.append(b);
        });
        sizes.hidden = false;
      } else {
        sizes.hidden = true;
      }
      pickVariant(firstAvailable ? firstAvailable.id : '');
    };

    // Open instantly from the card's embedded JSON, then read the current
    // Liquid payload in the background. Keeping a tab open must not freeze
    // yesterday's price, variants or gallery; a changed payload refills the
    // open dialog, an unchanged one leaves it untouched.
    let productRequest = 0;
    let productAbort = null;
    const refreshProduct = async (json, data, request) => {
      productAbort?.abort();
      const controller = new AbortController();
      productAbort = controller;
      const timer = setTimeout(() => controller.abort(), 4000);
      try {
        const url = new URL(data.url, window.location.origin);
        if (url.origin !== window.location.origin) return;
        url.searchParams.set('section_id', 'st-product-data');
        url.searchParams.set('_st_refresh', Date.now().toString());
        const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin', signal: controller.signal });
        const showing = () => request === productRequest && dialog.open && dialog.dataset.handle === data.handle;
        const doc = response.ok ? new DOMParser().parseFromString(await response.text(), 'text/html') : null;
        const payload = doc && doc.querySelector('script[data-qv]');
        if (response.status === 404 || response.status === 410 || (response.ok && !payload)) {
          // Product removed or unpublished since this page loaded.
          if (showing()) {
            addButton.disabled = true;
            addButton.textContent = addButton.dataset.labelSoldOut;
          }
          return;
        }
        if (!payload) return;
        const current = JSON.parse(payload.textContent);
        if (current.handle !== data.handle || typeof current.priceHtml !== 'string' || !Array.isArray(current.variants)) return;
        const fresh = JSON.stringify(current);
        // The next opening starts from the newest data, even offline.
        json.textContent = fresh;
        if (fresh !== JSON.stringify(data) && showing()) {
          fill(current);
          scheduleAutoplay();
        }
      } catch (error) {
        // Timeout or network failure: the embedded data stays on screen.
      } finally {
        clearTimeout(timer);
        if (productAbort === controller) productAbort = null;
      }
    };
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest('[data-quickview]');
      if (!trigger || !plainClick(event)) return;
      const card = trigger.closest('.product');
      const json = card && card.querySelector('script[data-qv]');
      if (!json) return;
      let data;
      try { data = JSON.parse(json.textContent); } catch (error) { return; }
      event.preventDefault();
      const request = ++productRequest;
      changePage(() => {
        opener = trigger;
        fill(data);
        dialog.showModal();
        scheduleAutoplay();
        document.body.classList.add('modal-open');
        track('quickview_open', { product_handle: data.handle });
        refreshProduct(json, data, request);
      });
    });
    dialog.querySelectorAll('[data-image-view]').forEach((button) => {
      button.addEventListener('click', () => showView(button.dataset.imageView === 'worn'));
    });
    dialog.querySelector('[data-qv-gallery-prev]')?.addEventListener('click', () => stepGallery(-1));
    dialog.querySelector('[data-qv-gallery-next]')?.addEventListener('click', () => stepGallery(1));
    playback?.addEventListener('click', () => {
      pausedByUser = !pausedByUser;
      updatePlayback();
      scheduleAutoplay();
    });
    galleryMedia?.addEventListener('st:product-zoom-change', () => {
      updatePlayback();
      scheduleAutoplay();
    });
    image.addEventListener('load', scheduleAutoplay);
    dialog.addEventListener('keydown', (event) => {
      if (!event.target.closest('.dialog-gallery') || event.altKey || event.ctrlKey || event.metaKey) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      stepGallery(event.key === 'ArrowRight' ? 1 : -1);
    });
    if (galleryMedia) {
      let touchX = null, touchY = null, touchTime = 0;
      galleryMedia.addEventListener('touchstart', (event) => {
        touchX = null;
        if (galleryMedia.classList.contains('is-product-zoomed') || event.target.closest('button, a') || event.touches.length !== 1) return;
        touchX = event.touches[0].clientX;
        touchY = event.touches[0].clientY;
        touchTime = Date.now();
        galleryGesture = true;
        stopAutoplay();
      }, { passive: true });
      galleryMedia.addEventListener('touchend', (event) => {
        const startX = touchX;
        touchX = null;
        galleryGesture = false;
        if (!galleryMedia.classList.contains('is-product-zoomed') && startX !== null && event.changedTouches.length) {
          const dx = event.changedTouches[0].clientX - startX;
          const dy = event.changedTouches[0].clientY - touchY;
          if (Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy) * 1.2 && Date.now() - touchTime < 900) showImage(imageIndex + (dx < 0 ? 1 : -1));
        }
        scheduleAutoplay();
      }, { passive: true });
      galleryMedia.addEventListener('touchcancel', () => {
        touchX = null;
        galleryGesture = false;
        scheduleAutoplay();
      }, { passive: true });
    }
    document.addEventListener('visibilitychange', scheduleAutoplay);
    window.addEventListener('pagehide', stopAutoplay);
    window.addEventListener('pageshow', scheduleAutoplay);
    reducedMotion.addEventListener('change', (event) => {
      if (!event.matches) return;
      pausedByUser = true;
      updatePlayback();
      stopAutoplay();
    });
    dialog.querySelector('.close-dialog')?.addEventListener('click', () => dialog.close());
    dialog.addEventListener('cancel', stopAutoplay);
    dialog.addEventListener('click', (event) => {
      const r = dialog.getBoundingClientRect();
      if (event.target === dialog && (event.clientX < r.left || event.clientX > r.right || event.clientY < r.top || event.clientY > r.bottom)) dialog.close();
    });
    dialog.addEventListener('close', () => {
      stopAutoplay();
      active = null;
      gallery = [];
      document.body.classList.remove('modal-open');
      opener?.focus({ preventScroll: true });
    });
    dialog.querySelector('#dialog-link')?.addEventListener('click', () => {
      if (active) track('quickview_pdp_click', { product_handle: active.handle });
    });
  }

  /* Campaign banner (6.2b): no pause button — the loop just plays. Reveal / placeholder fallback
     runs inline in sections/st-campaign.liquid; here only the 700px phone/desktop source swap. */
  const campaignVideo = document.querySelector('[data-campaign-video]');
  if (campaignVideo) {
    const banner = campaignVideo.closest('.campaign-banner');
    window.matchMedia('(max-width:700px)').addEventListener('change', () => {
      if (!banner || banner.classList.contains('is-fallback')) return;
      banner.classList.remove('is-playing');
      campaignVideo.load();
      campaignVideo.play().catch(() => {});
    });
    reducedMotion.addEventListener('change', (event) => {
      if (event.matches && banner) { banner.classList.remove('is-playing'); banner.classList.add('is-fallback'); campaignVideo.pause(); }
    });
  }

  /* SHOP —●— MORE rail: the bead follows the pointer ------------------------------- */
  document.querySelectorAll('.motion-rail').forEach((rail) => {
    const bead = rail.querySelector('.rolling-bead');
    if (!bead) return;
    rail.addEventListener('pointermove', (event) => {
      const rect = rail.getBoundingClientRect();
      bead.style.animation = 'none';
      bead.style.left = `${Math.max(0, Math.min(rect.width - 14, event.clientX - rect.left - 7))}px`;
    });
    rail.addEventListener('pointerleave', () => { bead.style.animation = ''; bead.style.left = ''; });
  });

  /* Interactive type (demo), scoped to the redesigned surfaces ---------------------- */
  const typeSelector = 'h1,h2,h3,h4,p,a,button,small,.product-info span,.object-number,.series-label>span,.footer-bottom>span';
  const skipSelector = '.product-photo,.series-tile,.archive-card';
  document.querySelectorAll('[data-st-interactive]').forEach((root) => {
    root.querySelectorAll(typeSelector).forEach((element) => {
      if (element.matches(skipSelector)) return;
      element.classList.add('interactive-type');
      if (!element.children.length || Array.from(element.children).every((child) => child.tagName === 'BR')) element.classList.add('type-leaf');
    });
  });

  /* Brand cursor (desktop mouse only; touch, keyboard and form fields keep native) -- */
  const cursorMedia = window.matchMedia('(hover: hover) and (pointer: fine) and (min-width:701px)');
  const brandCursor = document.createElement('div');
  brandCursor.className = 'brand-cursor';
  brandCursor.setAttribute('aria-hidden', 'true');
  document.body.append(brandCursor);
  const hideBrandCursor = () => {
    document.documentElement.classList.remove('brand-cursor-active');
    brandCursor.classList.remove('is-pressed');
  };
  const trackBrandCursor = (event) => {
    if (!cursorMedia.matches || event.pointerType !== 'mouse' || !(event.target instanceof Element)) { hideBrandCursor(); return; }
    if (event.target.closest('input,textarea,select,[contenteditable="true"],iframe,.jdgm-widget,.jm-review-widget')) { hideBrandCursor(); return; }
    const surface = event.target.closest('dialog[open]') || document.body;
    if (brandCursor.parentElement !== surface) surface.append(brandCursor);
    brandCursor.style.transform = `translate3d(${event.clientX}px,${event.clientY}px,0)`;
    brandCursor.classList.toggle('is-interactive', !!event.target.closest('a,button,summary,label,.interactive-type,.announcement,.bottom-band,[role="button"]'));
    document.documentElement.classList.add('brand-cursor-active');
  };
  document.addEventListener('pointermove', trackBrandCursor, { passive: true });
  document.addEventListener('pointerover', trackBrandCursor, { passive: true });
  document.addEventListener('pointerdown', (event) => { trackBrandCursor(event); if (event.pointerType === 'mouse') brandCursor.classList.add('is-pressed'); }, { passive: true });
  document.addEventListener('pointerup', () => brandCursor.classList.remove('is-pressed'), { passive: true });
  document.documentElement.addEventListener('pointerleave', hideBrandCursor);
  document.addEventListener('keydown', hideBrandCursor);
  document.addEventListener('visibilitychange', () => { if (document.hidden) hideBrandCursor(); });
  window.addEventListener('blur', hideBrandCursor);
  cursorMedia.addEventListener('change', hideBrandCursor);
  dialog?.addEventListener('close', () => { document.body.append(brandCursor); hideBrandCursor(); });

  /* Back to top (the sticky header is not a reliable #top target) ------------------- */
  document.querySelectorAll('a[href$="#top"]').forEach((link) => link.addEventListener('click', (event) => {
    event.preventDefault();
    window.scrollTo({ top: 0, behavior: behavior() });
  }));

  /* Same-page anchors (/#series, /#archive …) scroll instead of reloading the homepage */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('a[href*="#"]');
    if (!link || !plainClick(event) || link.hasAttribute('data-category') || link.hasAttribute('data-open-catalog')) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin || url.pathname !== location.pathname || !url.hash || url.hash === '#top') return;
    const target = document.getElementById(decodeURIComponent(url.hash.slice(1)));
    if (!target) return;
    event.preventDefault();
    closeShop();
    target.scrollIntoView({ behavior: behavior(), block: 'start' });
    history.replaceState(null, '', url.hash);
  });
})();

/* 6.3.1: one real founder-gift window per cart; never a repeating countdown. */
(() => {
  'use strict';
  /* ST_FOUNDER_GIFT_FACTORY_START */
  const createFounderGiftController = function (env) {
    const DAY = 24 * 60 * 60 * 1000;
    const START = 'Founder gift window started';
    const END = 'Founder gift window ends';
    const KEY = 'st-founder-gift-window-v1';
    let token = null;
    let record = null;
    let serverOffset = 0;
    let pendingStart = null;
    let lastAttempt = -Infinity;
    let queue = Promise.resolve();
    const clock = () => env.now() + serverOffset;
    const tokenOf = (cart) => typeof cart?.token === 'string' ? cart.token.split('?')[0] : '';
    const stamp = (value) => typeof value === 'string' && /^\d{4}-\d\d-\d\dT/.test(value) ? Date.parse(value) : NaN;
    const validRecord = (start, end) => Number.isFinite(start) && start > 0 && start <= clock() + 300000 && Number.isFinite(end) && end > start && end - start <= DAY;
    const fromAttributes = (attributes = {}) => {
      const start = stamp(attributes[START]);
      const end = stamp(attributes[END]);
      if (!Number.isFinite(start)) return null;
      const deadline = Number.isFinite(end) && end > start && end <= start + DAY ? end : start + DAY;
      return validRecord(start, deadline) ? { start, end: deadline } : null;
    };
    const readLocal = (cartToken) => {
      try {
        const saved = JSON.parse(env.storage?.getItem(KEY) || 'null');
        return saved?.token === cartToken && validRecord(saved.start, saved.end) ? { start: saved.start, end: saved.end } : null;
      } catch (_) { return null; }
    };
    const writeLocal = () => {
      if (!token || !record) return;
      try { env.storage?.setItem(KEY, JSON.stringify({ token, start: record.start, end: record.end })); } catch (_) { /* Cart attributes still recover it. */ }
    };
    const earliest = (records) => {
      const candidates = records.filter(Boolean).sort((a, b) => a.start - b.start || a.end - b.end);
      if (!candidates.length) return null;
      const first = candidates[0];
      return { start: first.start, end: Math.min(first.start + DAY, ...candidates.map((value) => value.end)) };
    };
    const updateClock = (date) => {
      const time = date && Date.parse(date);
      if (Number.isFinite(time)) serverOffset = time - env.now();
    };
    const snapshot = () => {
      if (!record) return { state: pendingStart === null ? 'idle' : 'pending', start: pendingStart, end: null, remaining: null };
      const remaining = Math.max(0, Math.ceil((record.end - clock()) / 1000));
      return { state: record.persisted ? (remaining > 0 ? 'active' : 'expired') : 'pending', start: record.start, end: record.end, remaining };
    };
    const render = () => {
      const state = snapshot();
      const hours = state.remaining === null ? 0 : Math.floor(state.remaining / 3600);
      const minutes = state.remaining === null ? 0 : Math.floor(state.remaining % 3600 / 60);
      const seconds = state.remaining === null ? 0 : state.remaining % 60;
      const time = state.state === 'active' ? `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} remaining`
        : state.state === 'expired' ? 'YOUR 24-HOUR WINDOW HAS ENDED'
        : state.state === 'pending' ? 'GIFT WINDOW PENDING'
        : '24H AFTER ADDING';
      /* 6.3.3: every qualifying order gets a gift (random among pieces of equal price; five from 6.3.4) — no "chance" copy. */
      const copy = state.state === 'expired' ? 'The 24-hour gift window for this cart has ended. You can still shop your objects.'
        : state.state === 'pending' ? 'Your item is in the cart. We’re confirming your gift window; refresh before checkout if this message remains.'
        : 'Add any SPACETRASH piece and our founder picks a piece for you, on us.';
      env.panels().forEach((panel) => {
        panel.dataset.state = state.state;
        const timer = panel.querySelector('[data-founder-gift-time]');
        const text = panel.querySelector('[data-founder-gift-copy]');
        const status = panel.querySelector('[data-founder-gift-status]');
        const fg = panel.dataset.fg || 'idle';
        const panelTime = fg === 'revealed' && state.state === 'active' ? time.replace('remaining', 'left to keep it')
          : fg === 'revealed' || fg === 'held' || state.state === 'expired' ? time
          : 'ONE GIFT PER ORDER';
        if (timer && timer.textContent !== panelTime) timer.textContent = panelTime;
        // 10-18: a one-piece pool names the gift (data-fg-idle-copy from st-founder-gift.liquid).
        const panelCopy = state.state === 'expired' || state.state === 'pending' ? copy : (panel.dataset.fgIdleCopy || copy);
        if (text && !panel.hasAttribute('data-copy-static') && text.textContent !== panelCopy) text.textContent = panelCopy;
        const announcement = `${state.state}:${state.start || ''}:${fg}`;
        if (panel.dataset.giftAnnouncement !== announcement) {
          panel.dataset.giftAnnouncement = announcement;
          if (status) status.textContent = fg === 'revealed' && state.state === 'active' ? 'Your founder’s gift is reserved for 24 hours.'
            : state.state === 'expired' ? 'Your founder gift window has ended.' : '';
        }
      });
      return state;
    };
    const serial = (work) => {
      const next = queue.then(work, work);
      queue = next.catch(() => {});
      return next;
    };
    const request = async (url, options = {}) => {
      const controller = new env.AbortController();
      const timeout = env.setTimeout(() => controller.abort(), 4000);
      try {
        const response = await env.fetch(url, { ...options, signal: controller.signal, cache: 'no-store', credentials: 'same-origin', headers: { Accept: 'application/json', ...(options.headers || {}) } });
        if (!response.ok) throw new Error('Cart promotion request failed');
        return { cart: await response.json(), date: response.headers?.get('Date') };
      } finally { env.clearTimeout(timeout); }
    };
    const applyCart = (cart, date) => {
      updateClock(date);
      const nextToken = tokenOf(cart);
      if (!nextToken) return false;
      if (nextToken !== token) { token = nextToken; record = null; }
      const remote = fromAttributes(cart.attributes);
      const pending = pendingStart === null ? null : { start: pendingStart, end: pendingStart + DAY };
      const selected = earliest([remote, readLocal(token), record, pending]);
      pendingStart = null;
      record = selected ? { ...selected, persisted: !!remote && remote.start === selected.start && remote.end === selected.end && cart.attributes?.[START] === new Date(selected.start).toISOString() && cart.attributes?.[END] === new Date(selected.end).toISOString() } : null;
      writeLocal();
      render();
      return true;
    };
    const persist = async (force = false) => {
      if (!record || record.persisted || !token || (!force && env.now() - lastAttempt < 15000)) return;
      lastAttempt = env.now();
      // Only these names are updated; other cart attributes, quantities and notes survive.
      const requested = { token, start: record.start, end: record.end };
      try {
        const result = await request(env.cartUrl + '/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: {
            [START]: new Date(requested.start).toISOString(),
            [END]: new Date(requested.end).toISOString(),
            '__Founder gift campaign': 'founder-gift-random-v2'
          } })
        });
        if (tokenOf(result.cart) !== requested.token || token !== requested.token) return;
        applyCart(result.cart, result.date);
      } catch (_) { render(); /* Keep the original pending time for the next retry. */ }
    };
    const syncCart = (cart, date) => serial(async () => {
      if (applyCart(cart, date)) await persist();
      return render();
    });
    const afterSuccessfulAdd = (date) => {
      const received = date && Date.parse(date);
      const successfulAt = Number.isFinite(received) ? received : clock();
      return serial(async () => {
        const begin = async () => {
          // Re-read the cart before writing, including changes made in another tab.
          try {
            const result = await request(env.cartUrl + '.js');
            if (!applyCart(result.cart, result.date || date)) { pendingStart = pendingStart === null ? successfulAt : Math.min(pendingStart, successfulAt); return render(); }
            if (!record) {
              const start = successfulAt;
              record = { start, end: start + DAY, persisted: false };
              writeLocal();
              render();
            }
            await persist(true);
          } catch (_) {
            // A confirmed cart identity is necessary; never invent a ticking window.
            if (token && !record) {
              updateClock(date);
              const start = successfulAt;
              record = { start, end: start + DAY, persisted: false };
              writeLocal();
            } else if (!token) {
              pendingStart = pendingStart === null ? successfulAt : Math.min(pendingStart, successfulAt);
            }
            render();
          }
          return render();
        };
        return env.lock ? env.lock('st-founder-gift-first-add', begin) : begin();
      });
    };
    const recover = () => serial(async () => {
      try {
        const result = await request(env.cartUrl + '.js');
        if (applyCart(result.cart, result.date)) await persist(true);
      } catch (_) { render(); }
      return render();
    });
    const interval = env.setInterval(render, 1000);
    return { syncCart, afterSuccessfulAdd, recover, render, snapshot, destroy: () => env.clearInterval(interval) };
  };
  /* ST_FOUNDER_GIFT_FACTORY_END */
  let storage = null;
  try { storage = window.localStorage; } catch (_) { /* Browsers may disable storage. */ }
  const offer = createFounderGiftController({
    now: () => Date.now(),
    fetch: (url, options) => fetch(url, options),
    storage,
    panels: () => Array.from(document.querySelectorAll('[data-founder-gift]')),
    cartUrl: (window.routes && window.routes.cart_url) || '/cart',
    AbortController,
    setTimeout: window.setTimeout.bind(window),
    clearTimeout: window.clearTimeout.bind(window),
    setInterval: window.setInterval.bind(window),
    clearInterval: window.clearInterval.bind(window),
    lock: navigator.locks?.request ? (name, work) => navigator.locks.request(name, work) : null
  });
  window.__stFounderGift = offer;
  offer.render();
  window.addEventListener('online', () => { offer.recover().catch(() => {}); });
  window.addEventListener('pageshow', (event) => { if (event.persisted) offer.recover().catch(() => {}); });
  window.addEventListener('storage', (event) => { if (event.key === 'st-founder-gift-window-v1') offer.recover().catch(() => {}); });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) offer.recover().catch(() => {}); });
})();

/* 6.3.3 (2026-10-05) Founder's gift reveal — the founder picks the gift, the customer does not.
   One draw per order (cart attribute "Founder gift drawn" = handle|variantId|ISO), no swaps, no re-rolls.
   - afterAdd(): after a successful add + drawer refresh. Draws if the cart qualifies and nothing was drawn yet;
     restores the SAME gift if it was set aside because no qualifying piece was left ("held").
   - sync(): after every drawer refresh. Sets the gift aside when no qualifying piece remains, and
     releases it when the 24-hour window (window controller above) has ended.
   - Removing the gift line on purpose marks "Founder gift released": it never comes back for this order.
   The $0 price is the automatic discount (bogo-eligible-auto → one of the three gifts); TT-SKU never qualifies. */
(() => {
  'use strict';
  const DRAWN = 'Founder gift drawn';
  const RELEASED = 'Founder gift released';
  const PROP = '_founder_gift';
  const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
  const addUrl = (window.routes && window.routes.cart_add_url) || '/cart/add';
  const changeUrl = (window.routes && window.routes.cart_change_url) || '/cart/change';
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let busy = false;

  const track = (name, params = {}) => {
    try {
      if (typeof window.gtag === 'function') window.gtag('event', name, params);
      if (typeof window.clarity === 'function') window.clarity('event', name);
    } catch (_) { /* analytics must never break the cart */ }
  };
  const postJSON = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST', credentials: 'same-origin', keepalive: true,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error(`${url} failed`);
    return response.json();
  };
  const setAttributes = (attributes) => postJSON(`${cartUrl}/update.js`, { attributes });
  const panel = () => document.querySelector('#cart-drawer [data-founder-gift]');
  const pool = (el) => { try { return JSON.parse(el.querySelector('[data-fg-pool]')?.textContent || '[]'); } catch (_) { return []; } };
  const randomIndex = (n) => {
    if (n <= 1) return 0;
    try { const a = new Uint32Array(1); crypto.getRandomValues(a); return a[0] % n; } catch (_) { return Math.floor(Math.random() * n); }
  };
  const windowExpired = () => window.__stFounderGift?.snapshot?.().state === 'expired';
  const refresh = () => (window.__stRefreshCart ? window.__stRefreshCart() : Promise.resolve());
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const addGift = async (gift, variantId) => {
    // Attribute first so a refresh cannot treat the new line as not-drawn and remove it.
    await setAttributes({ [DRAWN]: `${gift.handle}|${variantId}|${new Date().toISOString()}` });
    await postJSON(`${addUrl}.js`, { items: [{ id: Number(variantId), quantity: 1, properties: { [PROP]: gift.name } }] });
  };

  /* ---------- the show ---------- */
  const sparkBurst = (box) => {
    if (!box || reduceMotion()) return;
    box.innerHTML = '';
    for (let i = 0; i < 18; i += 1) {
      const s = document.createElement('span');
      const angle = (Math.PI * 2 * i) / 18 + Math.random() * 0.3;
      const dist = 60 + Math.random() * 70;
      s.style.setProperty('--dx', `${Math.cos(angle) * dist}px`);
      s.style.setProperty('--dy', `${Math.sin(angle) * dist}px`);
      s.style.setProperty('--d', `${520 + Math.random() * 380}ms`);
      s.className = i % 3 === 0 ? 'is-star' : '';
      box.append(s);
    }
    setTimeout(() => { box.innerHTML = ''; }, 1100);
  };

  const play = (el, items, winner) => new Promise((resolve) => {
    const stage = el.querySelector('[data-fg-stage]');
    const reel = el.querySelector('[data-fg-reel]');
    const crate = el.querySelector('[data-fg-crate]');
    const headline = el.querySelector('[data-fg-headline]');
    const skip = el.querySelector('[data-fg-skip]');
    if (!stage || !reel) { resolve(); return; }
    // Long strip: several laps through the pool, ending on the winner.
    const laps = reduceMotion() ? 0 : 6;
    const strip = [];
    for (let lap = 0; lap < laps; lap += 1) items.forEach((it) => strip.push(it));
    strip.push(winner);
    reel.innerHTML = strip.map((it) => `<div class="st-fg-slide"><img src="${it.image}" alt="" width="120" height="120" decoding="async"></div>`).join('');
    el.classList.add('is-revealing');
    stage.hidden = false;
    headline.textContent = items.length === 1 ? 'Unboxing your gift…' : 'The founder is choosing…';
    let done = false;
    const animations = [];
    const finish = () => {
      if (done) return;
      done = true;
      animations.forEach((a) => { try { a.finish(); } catch (_) {} });
      reel.style.transform = `translateX(${-(strip.length - 1) * 100}%)`;
      el.classList.add('is-landed');
      sparkBurst(el.querySelector('[data-fg-sparks]'));
      try { if (navigator.vibrate) navigator.vibrate([18, 40, 28]); } catch (_) {}
      headline.innerHTML = items.length === 1 ? `Your <strong>${winner.name}</strong> — on us.` : `The founder picked <strong>${winner.name}</strong> for you — on us.`;
      skip.hidden = true;
      resolve();
    };
    skip.hidden = reduceMotion();
    skip.onclick = finish;
    // Backgrounded tabs pause animations; never leave the customer waiting on a paused show.
    setTimeout(finish, 4500);
    stage.addEventListener('click', (e) => { if (!e.target.closest('[data-fg-sizes]')) finish(); }, { once: true });
    if (reduceMotion() || !reel.animate) { finish(); return; }
    // Images first (they are small and cached by the drawer), then shake → spin → settle.
    Promise.race([Promise.all([...reel.querySelectorAll('img')].map((img) => img.decode?.().catch(() => {}))), sleep(900)]).then(() => {
      if (done) return;
      const shake = crate.animate([
        { transform: 'rotate(0)' }, { transform: 'rotate(-6deg)' }, { transform: 'rotate(5deg)' },
        { transform: 'rotate(-4deg)' }, { transform: 'rotate(3deg)' }, { transform: 'scale(1.08)', opacity: 1 }, { transform: 'scale(.6)', opacity: 0 }
      ], { duration: 650, easing: 'ease-in-out', fill: 'forwards' });
      animations.push(shake);
      shake.onfinish = () => {
        if (done) return;
        el.classList.add('is-spinning');
        const spin = reel.animate([
          { transform: 'translateX(0)', filter: 'blur(0)' },
          { transform: `translateX(${-(strip.length - 1) * 60}%)`, filter: 'blur(2px)', offset: 0.55 },
          { transform: `translateX(${-(strip.length - 1) * 100}%)`, filter: 'blur(0)' }
        ], { duration: 1900, easing: 'cubic-bezier(.15,.75,.2,1)', fill: 'forwards' });
        animations.push(spin);
        spin.onfinish = finish;
      };
    });
  });

  const draw = async (el) => {
    const items = pool(el);
    if (!items.length) return;
    const winner = items[randomIndex(items.length)];
    // Lock the draw before the show: closing the drawer or reloading can never re-roll it.
    const needsSize = winner.variants.length > 1;
    const single = needsSize ? 'size' : winner.variants[0]?.id;
    if (!single) return;
    await setAttributes({ [DRAWN]: `${winner.handle}|${single}|${new Date().toISOString()}` });
    track('founder_gift_draw', { gift_handle: winner.handle, needs_size: needsSize ? 'true' : 'false' });
    // Size buttons are server-rendered on the next refresh. Don't wait on a click while busy.
    if (needsSize) el.dataset.fg = 'size';
    await play(el, items, winner);
    if (!needsSize) {
      await addGift(winner, single);
      track('founder_gift_reveal', { gift_handle: winner.handle, gift_variant: String(single) });
      await sleep(reduceMotion() ? 0 : 900);
    }
    await refresh();
  };

  const restore = async (el) => {
    const [handle, variant] = String(el.dataset.fgDrawn || '').split('|');
    const gift = pool(el).find((g) => g.handle === handle);
    const variantId = gift && gift.variants.find((v) => String(v.id) === String(variant))?.id;
    // A missing size is the liquid "size" state. Never block here waiting for a click.
    if (!gift || !variantId) return;
    await addGift(gift, variantId);
    track('founder_gift_restore', { gift_handle: gift.handle });
    await refresh();
  };

  const afterAdd = async () => {
    if (busy) return;
    const el = panel();
    if (!el || el.dataset.fgEligible !== 'true') return;
    if (el.dataset.fg === 'size' || el.dataset.fg === 'held') return;
    busy = true;
    try {
      if (el.dataset.fg === 'idle' && !windowExpired()) await draw(el);
      else if (el.dataset.fg === 'restore' && !windowExpired()) await restore(el);
    } catch (_) {
      // The paid item is already in the cart; a failed gift step must not look like a failed add.
      el.classList.remove('is-revealing', 'is-landed', 'is-spinning');
      el.querySelector('[data-fg-stage]')?.setAttribute('hidden', '');
    } finally { busy = false; }
  };

  const sizeRootFor = (form) => {
    const drawer = form.closest && form.closest('#cart-drawer');
    if (drawer) return drawer.querySelector('[data-founder-gift][data-fg="size"]');
    return form.querySelector('[data-founder-gift][data-fg="size"]');
  };
  const focusSize = (root) => {
    if (!root) return;
    const hint = root.querySelector('[data-fg-size-hint]');
    if (hint) hint.hidden = false;
    const picker = root.querySelector('[data-fg-size-picker]');
    try { picker?.scrollIntoView({ block: 'nearest' }); } catch (_) {}
    picker?.querySelector('[data-fg-size]')?.focus();
  };

  // Size buttons survive drawer refreshes because the listener is on document, not the node.
  document.addEventListener('click', async (event) => {
    const t = event.target instanceof Element ? event.target : null;
    const sizeBtn = t && t.closest('[data-fg-size]');
    if (!sizeBtn || sizeBtn.disabled) return;
    const root = sizeBtn.closest('[data-founder-gift]');
    if (!root || root.dataset.fg !== 'size') return;
    event.preventDefault();
    const handle = String(root.dataset.fgDrawn || '').split('|')[0];
    const gift = pool(root).find((g) => g.handle === handle);
    const variantId = sizeBtn.dataset.fgSize;
    if (!gift || !variantId) return;
    root.querySelectorAll('[data-fg-size], [data-fg-skip-gift]').forEach((x) => { x.disabled = true; });
    try {
      if (windowExpired()) {
        // 10-12: the page sat open past the 24 hours — end the gift, don't add a line the guard removes.
        await setAttributes({ [RELEASED]: `expired ${new Date().toISOString()}` });
        track('founder_gift_guard', { reason: 'expired_unclaimed' });
        if (document.body.classList.contains('template-cart')) { window.location.reload(); return; }
        await refresh();
        return;
      }
      await addGift(gift, variantId);
      track('founder_gift_size_pick', { gift_handle: gift.handle, size: sizeBtn.dataset.fgSizeLabel || '' });
      track('founder_gift_reveal', { gift_handle: gift.handle, gift_variant: String(variantId) });
      if (document.body.classList.contains('template-cart')) {
        window.location.reload();
        return;
      }
      await refresh();
    } catch (_) {
      root.querySelectorAll('[data-fg-size], [data-fg-skip-gift]').forEach((x) => { x.disabled = false; });
    }
  });

  document.addEventListener('click', async (event) => {
    const t = event.target instanceof Element ? event.target : null;
    const skip = t && t.closest('[data-fg-skip-gift]');
    if (!skip || skip.disabled) return;
    event.preventDefault();
    const root = skip.closest('[data-founder-gift]');
    const handle = String(root?.dataset.fgDrawn || '').split('|')[0];
    skip.disabled = true;
    try {
      await setAttributes({ [RELEASED]: `skipped ${new Date().toISOString()}` });
      track('founder_gift_skip', { gift_handle: handle });
      document.querySelectorAll('[data-founder-gift][data-fg="size"]').forEach((el) => { el.dataset.fg = 'released'; });
      const form = skip.closest('form') || document.querySelector('#cart-drawer form, form.cart-layout');
      const checkout = form && form.querySelector('[name="checkout"]');
      if (checkout) checkout.click();
      else if (form && form.requestSubmit) form.requestSubmit();
    } catch (_) { skip.disabled = false; }
  });

  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (!form || form.nodeName !== 'FORM' || !form.querySelector('[name="checkout"]')) return;
    const root = sizeRootFor(form);
    if (!root) return;
    event.preventDefault();
    focusSize(root);
  }, true);

  // Cart guard (runs after every drawer refresh and on page load). Option C: the gift is a hidden $0 product
  // (product_type "Founder Gift"), so the only way to a free piece is the draw. The guard removes any $0 gift that
  // did not come from this order's draw, keeps one at quantity 1, swaps old-flow lines (real product + founder
  // property) to the $0 gift, sets the gift aside when no paid SPACETRASH piece is left, and releases it at expiry.
  const GIFT_TYPE = 'Founder Gift';
  const sync = async (cart) => {
    if (busy || !cart) return;
    const el = panel();
    const items = cart.items || [];
    const drawnVariant = String((cart.attributes || {})[DRAWN] || '').split('|')[1] || '';
    const actions = [];
    let kept = null;
    let swap = null;
    items.forEach((line) => {
      const marked = Boolean(line.properties && line.properties[PROP]);
      if (line.product_type === GIFT_TYPE) {
        if (!marked || String(line.variant_id) !== drawnVariant || kept) { actions.push({ id: line.key, quantity: 0, why: 'not_drawn' }); return; }
        kept = line;
        if (line.quantity > 1) actions.push({ id: line.key, quantity: 1, why: 'quantity' });
      } else if (marked) {
        actions.push({ id: line.key, quantity: 0, why: 'old_flow' });
        const gift = el && pool(el).find((g) => g.handle === line.handle);
        const variant = gift && (gift.variants.find((v) => v.title === line.variant_title) || (gift.variants.length === 1 ? gift.variants[0] : null));
        if (gift && variant) swap = { gift, id: variant.id };
      }
    });
    const expired = windowExpired();
    const eligible = el ? el.dataset.fgEligible === 'true' : true;
    if (kept && (expired || !eligible)) actions.push({ id: kept.key, quantity: 0, why: expired ? 'expired' : 'held' });
    // 10-12: a drawn gift that never reached the cart (ring size not picked, or set aside) also ends with
    // the window. Without this the card kept asking for a size, gated checkout, and a picked gift was
    // added and then removed again.
    const attrs = cart.attributes || {};
    const unclaimedExpired = expired && !kept && Boolean(attrs[DRAWN]) && !attrs[RELEASED];
    if (!actions.length && !unclaimedExpired) return;
    busy = true;
    try {
      for (const a of actions) {
        await postJSON(`${changeUrl}.js`, { id: a.id, quantity: a.quantity });
        if (a.why !== 'held' && a.why !== 'quantity') track('founder_gift_guard', { reason: a.why });
      }
      if ((expired && kept) || unclaimedExpired) await setAttributes({ [RELEASED]: `expired ${new Date().toISOString()}` });
      if (unclaimedExpired) track('founder_gift_guard', { reason: 'expired_unclaimed' });
      if (swap && !kept && !expired) await addGift(swap.gift, swap.id);
      busy = false;
      await refresh();
    } catch (_) { /* next refresh retries */ } finally { busy = false; }
  };

  // Removing the gift on purpose = released for this order (drawer button or cart page).
  document.addEventListener('click', (event) => {
    const t = event.target instanceof Element ? event.target : null;
    const hit = t && (t.closest('[data-founder-remove]') || t.closest('[data-founder-line] cart-remove-button'));
    if (!hit) return;
    setAttributes({ [RELEASED]: `removed ${new Date().toISOString()}` }).catch(() => {});
    track('founder_gift_release', {});
  }, true);

  // Window can run out while the page sits open.
  setInterval(() => {
    if (!windowExpired() || !panel() || !['revealed', 'size', 'restore'].includes(panel().dataset.fg)) return;
    fetch(`${cartUrl}.js`, { cache: 'no-store' }).then((r) => r.json()).then(sync).catch(() => {});
  }, 30000);

  // A known variant that never made it into the cart is added back. A missing ring size is not:
  // those buttons are already in the HTML, and waiting on them used to leave `busy` stuck.
  window.addEventListener('load', async () => {
    try { await sync(await fetch(`${cartUrl}.js`, { cache: 'no-store' }).then((r) => r.json())); } catch (_) {}
    const el = panel();
    if (el && el.dataset.fg === 'restore' && el.dataset.fgEligible === 'true') afterAdd();
    // 10-18: drawn from an older gift pool and never claimed → draw again from the current pool.
    else if (el && el.dataset.fgStale === 'true' && el.dataset.fg === 'idle' && el.dataset.fgEligible === 'true') afterAdd();
  }, { once: true });

  window.__stFounderReveal = { afterAdd, sync };
})();


